import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown,
  Minus,
  Search,
  BarChart3,
  Building,
  Loader2,
  Columns3,
  GripVertical,
  AlertTriangle,
  Flame,
  Target,
  Thermometer,
  Snowflake,
  XCircle,
  Phone,
  Shield,
  DollarSign,
  Trophy,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  Calendar,
  UserCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEAL_STAGE_COLUMNS = [
  { key: 'Hot Deal', label: 'Hot Deal', dot: 'bg-orange-500' },
  { key: 'SQL', label: 'SQL', dot: 'bg-emerald-500' },
  { key: 'Warm Lead', label: 'Warm Lead', dot: 'bg-amber-500' },
  { key: 'Cold Lead', label: 'Cold Lead', dot: 'bg-blue-500' },
  { key: 'Disqualified', label: 'Disqualified', dot: 'bg-red-500' },
];

const CALL_STAGE_COLUMNS = [
  { key: 'Discovery', label: 'Discovery', dot: 'bg-blue-500' },
  { key: 'Qualification', label: 'Qualification', dot: 'bg-violet-500' },
  { key: 'Demo', label: 'Demo', dot: 'bg-amber-500' },
  { key: 'Negotiation', label: 'Negotiation', dot: 'bg-orange-500' },
  { key: 'Closing', label: 'Closing', dot: 'bg-emerald-500' },
];

// ─── Deal Card ─────────────────────────────────────────
function DealCard({ deal, onDragStart, isManager, onClick, formatValue }) {
  const scoreColor = deal.score >= 75 ? 'text-emerald-600' : deal.score >= 50 ? 'text-amber-600' : 'text-red-600';
  const riskLabel = deal.risk_status || (deal.risk >= 60 ? 'High' : deal.risk >= 30 ? 'Medium' : 'Low');
  const riskColor = riskLabel === 'High' || riskLabel === 'Critical' ? 'text-red-600' : riskLabel === 'Medium' ? 'text-amber-600' : 'text-emerald-600';

  // Parse stage_breakdown or fall back to total_calls
  const stages = deal.stage_breakdown || {};
  const stageStr = Object.entries(stages).map(([s, c]) => `${c} ${s.toLowerCase()}`).join(' \u00b7 ') || `${deal.total_calls || 0} call${(deal.total_calls || 0) !== 1 ? 's' : ''}`;

  const nextStep = deal.next_steps?.[0] || null;
  const hasNoNextStep = !nextStep && deal.has_analysis;
  const hasDM = deal.calls?.some(c => c.decision_maker === 'Yes');

  return (
    <div
      draggable={isManager}
      onDragStart={(e) => onDragStart(e, deal)}
      onClick={() => onClick(deal)}
      className={`group p-3.5 rounded-lg bg-white border border-border/50 hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer ${isManager ? 'active:cursor-grabbing' : ''}`}
      data-testid={`kanban-card-${deal.company}`}
    >
      {/* Row 1: Name + Value */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="font-medium text-sm text-foreground truncate leading-tight">{deal.company}</span>
        {deal.deal_value ? (
          <span className="text-sm font-semibold text-foreground shrink-0">{formatValue(deal.deal_value)}</span>
        ) : null}
      </div>

      {/* Row 2: Score + Risk */}
      <div className="flex items-center gap-3 mb-2.5 text-xs">
        <span className={`font-semibold ${scoreColor}`}>
          Score: {deal.score}
          {deal.trend === 'improving' ? ' \u2191' : deal.trend === 'declining' ? ' \u2193' : ''}
        </span>
        <span className={`${riskColor}`}>Risk: {riskLabel}</span>
      </div>

      {/* Row 3: Next step */}
      <div className="mb-2">
        {nextStep ? (
          <p className="text-xs text-muted-foreground truncate">
            <span className="text-foreground/70 font-medium">Next:</span> {nextStep.length > 55 ? nextStep.slice(0, 55) + '...' : nextStep}
          </p>
        ) : hasNoNextStep ? (
          <p className="text-xs text-amber-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            No next step defined
          </p>
        ) : null}
      </div>

      {/* Row 4: Call breakdown + indicators */}
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span className="truncate">{stageStr}</span>
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          {hasDM && <UserCheck className="w-3 h-3 text-emerald-600" title="Decision maker confirmed" />}
          {deal.risk_count > 0 && <AlertTriangle className="w-3 h-3 text-amber-600" title={`${deal.risk_count} risks`} />}
          {deal.outcome === 'won' && <Trophy className="w-3 h-3 text-emerald-600" />}
          {deal.outcome === 'lost' && <XCircle className="w-3 h-3 text-red-600" />}
        </div>
      </div>
    </div>
  );
}

// ─── Kanban Column ─────────────────────────────────────
function KanbanColumn({ column, deals, onDrop, onDragOver, onDragStart, isManager, onClickDeal, formatValue }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const totalValue = deals.reduce((s, d) => s + (d.deal_value || 0), 0);
  const avgScore = deals.length > 0 ? Math.round(deals.reduce((s, d) => s + (d.score || 0), 0) / deals.length) : 0;

  return (
    <div
      className={`flex flex-col min-w-[240px] flex-1 rounded-lg transition-all ${isDragOver ? 'ring-2 ring-primary/30 bg-primary/[0.02]' : ''}`}
      onDragOver={(e) => { e.preventDefault(); onDragOver(e); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => { onDrop(e, column.key); setIsDragOver(false); }}
      data-testid={`kanban-column-${column.key}`}
    >
      {/* Column header */}
      <div className="px-1 pb-3">
        <div className="flex items-center gap-2 mb-0.5">
          <div className={`w-2 h-2 rounded-full ${column.dot}`} />
          <span className="text-sm font-semibold text-foreground">{column.label}</span>
          <span className="text-xs text-muted-foreground ml-auto">{deals.length}</span>
        </div>
        <p className="text-[11px] text-muted-foreground pl-4">
          {formatValue(totalValue)} &middot; {deals.length} deal{deals.length !== 1 ? 's' : ''} &middot; Avg {avgScore}
        </p>
      </div>

      {/* Cards */}
      <div className="space-y-2 min-h-[80px] max-h-[calc(100vh-320px)] overflow-y-auto px-0.5 pb-1">
        {deals.length > 0 ? deals.map((deal, idx) => (
          <DealCard
            key={`${deal.company}-${idx}`}
            deal={deal}
            onDragStart={onDragStart}
            isManager={isManager}
            onClick={onClickDeal}
            formatValue={formatValue}
          />
        )) : (
          <div className="flex items-center justify-center h-16 text-[11px] text-muted-foreground/40 border border-dashed border-border/30 rounded-lg">
            No deals
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Pipeline Component ───────────────────────────
export default function Pipeline() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [forecast, setForecast] = useState(null);
  const [dashboardDeals, setDashboardDeals] = useState([]);
  const [deals, setDeals] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [companyPatterns, setCompanyPatterns] = useState(null);
  const [loadingForecast, setLoadingForecast] = useState(true);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [autoCreating, setAutoCreating] = useState(false);
  const [dealsSummary, setDealsSummary] = useState(null);
  const [pipelineType, setPipelineType] = useState('deal_stages');
  const isManager = user?.role === 'manager';

  useEffect(() => {
    fetchForecast();
    fetchDeals();
    fetchDealsSummary();
    fetchDashboardDeals();
  }, []);

  const fetchForecast = async () => {
    try {
      const res = await fetch(`${API_URL}/api/patterns/pipeline-forecast`, { credentials: 'include' });
      if (res.ok) setForecast(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingForecast(false); }
  };

  const fetchDeals = async () => {
    try {
      const res = await fetch(`${API_URL}/api/deals`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setDeals(data.deals || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchDealsSummary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/dashboard/deals/summary`, { credentials: 'include' });
      if (res.ok) setDealsSummary(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchDashboardDeals = async () => {
    try {
      const res = await fetch(`${API_URL}/api/dashboard/deals`, { credentials: 'include' });
      if (res.ok) setDashboardDeals(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleAutoCreate = async () => {
    setAutoCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/deals/auto-create`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created ${data.created} deals`);
        fetchDeals();
      }
    } catch (e) { toast.error('Failed to create deals'); }
    finally { setAutoCreating(false); }
  };

  const handleClickDeal = (deal) => {
    const matched = deals.find(d => d.company_name?.toLowerCase() === deal.company?.toLowerCase());
    if (matched) {
      navigate(`/deal/${matched.deal_id}`);
    } else if (deal.call_id) {
      navigate(`/report/${deal.call_id}`);
    }
  };

  const searchCompany = useCallback(async () => {
    if (!companySearch.trim()) return;
    setLoadingCompany(true);
    try {
      const res = await fetch(`${API_URL}/api/patterns/company/${encodeURIComponent(companySearch)}`, { credentials: 'include' });
      if (res.ok) setCompanyPatterns(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingCompany(false); }
  }, [companySearch]);

  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e, newKey) => {
    e.preventDefault();
    if (!draggedDeal || !isManager) return;

    const callId = draggedDeal.call_id;

    if (pipelineType === 'deal_stages' && draggedDeal.grade !== newKey) {
      // Optimistic update for deal grade
      setDashboardDeals(prev => prev.map(d => d.company === draggedDeal.company ? { ...d, grade: newKey } : d));
      if (forecast) {
        setForecast(prev => {
          if (!prev) return prev;
          const updated = prev.pipeline.map(d => d.call_id === callId ? { ...d, grade: newKey } : d);
          return { ...prev, pipeline: updated };
        });
      }

      try {
        const res = await fetch(`${API_URL}/api/patterns/deal-grade`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ call_id: callId, grade: newKey })
        });
        if (res.ok) { toast.success(`${draggedDeal.company} moved to ${newKey}`); }
        else { toast.error('Failed to update'); fetchForecast(); fetchDashboardDeals(); }
      } catch { toast.error('Failed to update'); fetchForecast(); fetchDashboardDeals(); }
    }

    setDraggedDeal(null);
  };

  const formatValue = (v) => {
    if (!v) return '$0';
    if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
    return `$${v.toLocaleString()}`;
  };

  // Group deals based on pipeline type
  const getColumns = () => pipelineType === 'deal_stages' ? DEAL_STAGE_COLUMNS : CALL_STAGE_COLUMNS;

  const getDealsForColumn = (columnKey) => {
    if (pipelineType === 'deal_stages') {
      return dashboardDeals.filter(d => d.grade === columnKey);
    }
    // Call stages: group by the latest call's deal_stage
    return dashboardDeals.filter(d => {
      const latestStage = d.calls?.[0]?.deal_stage || 'Discovery';
      return latestStage === columnKey;
    });
  };

  const totalPipelineValue = dashboardDeals.reduce((s, d) => s + (d.deal_value || 0), 0);
  const totalDeals = dashboardDeals.length;

  const getGradeColor = (grade) => {
    const colors = { 'Hot Deal': 'text-orange-600', 'SQL': 'text-emerald-600', 'Warm Lead': 'text-amber-600', 'Cold Lead': 'text-blue-600', 'Disqualified': 'text-red-600' };
    return colors[grade] || 'text-muted-foreground';
  };

  const getGradeBadge = (grade) => {
    const styles = {
      'Hot Deal': 'bg-orange-500/15 text-orange-600 border-orange-500/30',
      'SQL': 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
      'Warm Lead': 'bg-yellow-500/15 text-amber-600 border-yellow-500/30',
      'Cold Lead': 'bg-blue-500/15 text-blue-600 border-blue-500/30',
      'Disqualified': 'bg-red-500/15 text-red-600 border-red-500/30'
    };
    return styles[grade] || 'bg-secondary text-muted-foreground';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp className="w-4 h-4 text-emerald-600" />;
    if (trend === 'declining') return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  return (
    <Layout>
      <div className="space-y-5" data-testid="pipeline-page">
        {/* ─── Top Header Bar ─── */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-heading text-2xl font-bold text-foreground">Pipeline</h1>
            <Select value={pipelineType} onValueChange={setPipelineType}>
              <SelectTrigger className="w-44 h-8 text-xs bg-white border-border" data-testid="pipeline-type-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="deal_stages">Deal Stages</SelectItem>
                <SelectItem value="call_stages">Call Stages</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Compact global stats */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground" data-testid="pipeline-global-stats">
            <span>Total: <span className="font-semibold text-foreground">{formatValue(totalPipelineValue)}</span></span>
            <span className="text-border">|</span>
            <span>{totalDeals} deals</span>
            {dealsSummary && (
              <>
                <span className="text-border">|</span>
                <span className="text-emerald-600 font-medium">{dealsSummary.won.count} Won</span>
                <span className="text-border">|</span>
                <span className="text-red-600 font-medium">{dealsSummary.lost.count} Lost</span>
              </>
            )}
          </div>
        </div>

        <Tabs defaultValue="kanban" className="w-full">
          <TabsList className="bg-white/60 border border-border/50">
            <TabsTrigger value="kanban" className="data-[state=active]:bg-primary/10 text-xs" data-testid="kanban-tab">
              Kanban
            </TabsTrigger>
            <TabsTrigger value="pipeline" className="data-[state=active]:bg-primary/10 text-xs">
              Forecast
            </TabsTrigger>
            <TabsTrigger value="company" className="data-[state=active]:bg-primary/10 text-xs">
              Patterns
            </TabsTrigger>
          </TabsList>

          {/* ─── Kanban Board Tab ─── */}
          <TabsContent value="kanban" className="mt-4" data-testid="kanban-board">
            {loadingForecast && dashboardDeals.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : dashboardDeals.length > 0 ? (
              <div className="space-y-3">
                {isManager && (
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <GripVertical className="w-3 h-3" />
                    Drag and drop deals between columns to update their stage
                  </p>
                )}

                {/* Kanban columns */}
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {getColumns().map(col => (
                    <KanbanColumn
                      key={col.key}
                      column={col}
                      deals={getDealsForColumn(col.key)}
                      onDrop={handleDrop}
                      onDragOver={(e) => e.preventDefault()}
                      onDragStart={handleDragStart}
                      isManager={isManager && pipelineType === 'deal_stages'}
                      onClickDeal={handleClickDeal}
                      formatValue={formatValue}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-16 text-muted-foreground">
                <Columns3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>No deals in pipeline yet</p>
                <p className="text-sm mt-1">Upload and analyze calls to build your pipeline</p>
                {isManager && (
                  <Button size="sm" variant="outline" className="mt-3" onClick={handleAutoCreate} disabled={autoCreating}>
                    {autoCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                    Auto-create Deals from Calls
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          {/* ─── Pipeline Forecast Tab ─── */}
          <TabsContent value="pipeline" className="mt-6 space-y-6">
            {loadingForecast ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : forecast ? (
              <>
                <div className="grid gap-4 md:grid-cols-4">
                  <Card className="bg-card border-border/50">
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground">Total Deals</p>
                      <p className="font-heading text-2xl font-bold text-foreground">{forecast.forecast.total_deals}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-border/50">
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground">Pipeline Value</p>
                      <p className="font-heading text-2xl font-bold text-emerald-600" data-testid="forecast-pipeline-value">{formatValue(totalPipelineValue)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-border/50">
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground">Won Deals</p>
                      <div className="flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-emerald-600" />
                        <p className="font-heading text-2xl font-bold text-emerald-600" data-testid="forecast-won-count">{dealsSummary?.won?.count || 0}</p>
                      </div>
                      {dealsSummary?.won?.value > 0 && <p className="text-xs text-emerald-600/70 mt-0.5">{formatValue(dealsSummary.won.value)}</p>}
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-border/50">
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground">Lost Deals</p>
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <p className="font-heading text-2xl font-bold text-red-600" data-testid="forecast-lost-count">{dealsSummary?.lost?.count || 0}</p>
                      </div>
                      {dealsSummary?.lost?.value > 0 && <p className="text-xs text-red-600/70 mt-0.5">{formatValue(dealsSummary.lost.value)}</p>}
                    </CardContent>
                  </Card>
                </div>

                {forecast.stage_forecast?.length > 0 && (
                  <Card className="bg-card border-border/50" data-testid="stage-forecast-card">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Revenue Forecast by Stage</CardTitle>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Weighted Total:</span>
                          <span className="text-sm font-bold text-emerald-600" data-testid="weighted-pipeline-value">
                            {formatValue(forecast.forecast.weighted_pipeline_value || 0)}
                          </span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {forecast.stage_forecast.map((stage) => {
                          const maxVal = Math.max(...forecast.stage_forecast.map(s => s.total_value || 1));
                          const pct = maxVal > 0 ? (stage.total_value / maxVal) * 100 : 0;
                          return (
                            <div key={stage.stage} className="space-y-1" data-testid={`stage-row-${stage.stage}`}>
                              <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-foreground w-28">{stage.stage}</span>
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-border/50">
                                    {stage.deal_count} {stage.deal_count === 1 ? 'deal' : 'deals'}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">({Math.round(stage.weight * 100)}%)</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-muted-foreground text-xs">{formatValue(stage.total_value)}</span>
                                  <span className="text-emerald-600 font-medium w-16 text-right">{formatValue(stage.weighted_value)}</span>
                                </div>
                              </div>
                              <div className="h-1.5 rounded-full bg-secondary/50 overflow-hidden">
                                <div className="h-full rounded-full bg-primary/70 transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {forecast.rep_breakdown?.length > 0 && (
                  <Card className="bg-card border-border/50" data-testid="rep-breakdown-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Pipeline by Rep</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {forecast.rep_breakdown.map((rep, idx) => {
                          const maxW = Math.max(...forecast.rep_breakdown.map(r => r.weighted_value || 1));
                          const pct = maxW > 0 ? (rep.weighted_value / maxW) * 100 : 0;
                          return (
                            <div key={rep.user_id} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border/50" data-testid={`rep-row-${idx}`}>
                              <div className="w-7 h-7 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center text-xs font-semibold text-primary shrink-0">{idx + 1}</div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium text-foreground truncate">{rep.name}</span>
                                  <div className="flex items-center gap-3 text-xs shrink-0">
                                    <span className="text-muted-foreground">{rep.deal_count} deals</span>
                                    <span className="text-emerald-600 font-semibold">{formatValue(rep.weighted_value)}</span>
                                  </div>
                                </div>
                                <div className="h-1.5 rounded-full bg-secondary/50 overflow-hidden">
                                  <div className="h-full rounded-full bg-primary/70 transition-all" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <p className="text-center text-muted-foreground py-16">No pipeline data available</p>
            )}
          </TabsContent>

          {/* ─── Company Patterns Tab ─── */}
          <TabsContent value="company" className="mt-6 space-y-6">
            <div className="flex gap-2">
              <Input
                placeholder="Search company name..."
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchCompany()}
                className="bg-background border-border"
                data-testid="company-search-input"
              />
              <Button onClick={searchCompany} disabled={loadingCompany} data-testid="company-search-btn">
                {loadingCompany ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {companyPatterns && companyPatterns.total_calls > 0 ? (
              <>
                <Card className="bg-card border-border/50">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-heading text-lg font-bold text-foreground">{companyPatterns.company}</h3>
                        <p className="text-sm text-muted-foreground">{companyPatterns.total_calls} calls | {companyPatterns.first_contact} — {companyPatterns.latest_contact}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {getTrendIcon(companyPatterns.trend_direction)}
                        <span className={`font-heading text-2xl font-bold ${getGradeColor(companyPatterns.latest_grade)}`}>{companyPatterns.latest_score}</span>
                        <Badge className={`${getGradeBadge(companyPatterns.latest_grade)} border`}>{companyPatterns.latest_grade}</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Score change:</span>
                      <span className={companyPatterns.score_delta > 0 ? 'text-emerald-600' : companyPatterns.score_delta < 0 ? 'text-red-600' : 'text-muted-foreground'}>
                        {companyPatterns.score_delta > 0 ? '+' : ''}{companyPatterns.score_delta} pts
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Score Trend Over Time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {companyPatterns.patterns.score_trend?.map((point, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 rounded bg-secondary/20">
                          <span className="text-xs text-muted-foreground w-20">{point.date}</span>
                          <div className="flex-1"><Progress value={point.score} className="h-2" /></div>
                          <span className={`text-sm font-bold w-8 text-right ${getGradeColor(point.grade)}`}>{point.score}</span>
                          <Badge className={`${getGradeBadge(point.grade)} border text-xs`}>{point.grade}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Category Score Trends</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-3">
                      {Object.entries(companyPatterns.patterns.scoring_trends || {}).map(([key, values]) => {
                        if (!values || values.length === 0) return null;
                        const latest = values[values.length - 1]?.score || 0;
                        const first = values[0]?.score || 0;
                        const delta = latest - first;
                        const labels = { intent: 'Intent', budget: 'Budget', authority: 'Authority', timeline: 'Timeline', icp_fit: 'ICP Fit', risk: 'Risk' };
                        return (
                          <div key={key} className="p-3 rounded-lg bg-secondary/30 border border-border/50">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-foreground">{labels[key] || key}</span>
                              <span className="text-sm font-bold text-foreground">{latest}/5</span>
                            </div>
                            <div className="flex gap-0.5 mb-1">
                              {[1,2,3,4,5].map(i => (
                                <div key={i} className={`flex-1 h-1.5 rounded-full ${i <= latest ? 'bg-primary' : 'bg-secondary'}`} />
                              ))}
                            </div>
                            <span className={`text-xs ${delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                              {delta > 0 ? '+' : ''}{delta} from first call
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {Object.keys(companyPatterns.patterns.recurring_objections || {}).length > 0 && (
                  <Card className="bg-card border-border/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Recurring Objections</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(companyPatterns.patterns.recurring_objections).map(([type, data]) => (
                          <div key={type} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50">
                            <div>
                              <p className="font-medium text-foreground text-sm">{type}</p>
                              <p className="text-xs text-muted-foreground">Avg severity: {data.avg_severity}/10</p>
                            </div>
                            <Badge variant="outline" className="border-yellow-500/30 text-amber-600">{data.count}x</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-card border-border/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Sentiment & Tone Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {companyPatterns.patterns.sentiment_trend?.map((point, idx) => {
                        const sentColor = { 'Very Positive': 'text-emerald-600', 'Positive': 'text-emerald-600', 'Neutral': 'text-muted-foreground', 'Negative': 'text-red-600' };
                        return (
                          <div key={idx} className="flex items-center gap-3 p-2 rounded bg-secondary/20">
                            <span className="text-xs text-muted-foreground w-20">{point.date}</span>
                            <span className={`text-sm ${sentColor[point.sentiment] || 'text-muted-foreground'}`}>{point.sentiment}</span>
                            <span className="text-xs text-muted-foreground">({point.tone})</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : companyPatterns ? (
              <p className="text-center text-muted-foreground py-16">No calls found for "{companySearch}"</p>
            ) : null}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
